import { ROLE_LABEL, type Session } from "@app/session";
import { openFeedback, openPalette } from "@app/ui";
import { useAuthContext } from "@contexts/AuthContext";
import { Menu, Popover, Skeleton, useMantineColorScheme } from "@repo/ui/core";
import { useElementSize } from "@repo/ui/hooks";
import {
  IconBrandGitlab,
  IconBuildingPlus,
  IconCheck,
  IconLogout,
  IconMessage2,
  IconMoon,
  IconSearch,
  IconSelector,
  IconShieldCheck,
  IconSlash,
  IconSun,
  IconUser,
} from "@repo/ui/icons";
import { Kbd, StatusDot } from "@repo/ui/na";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useAnalytics } from "../analytics";
import type { Crumb } from "../nav";
import classes from "./shell.module.scss";

const initialsOf = (s?: string) =>
  (s ?? "?")
    .split(/[\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

/** Below this header width the search, running and tenant controls compact. */
const NARROW = 860;

type Props = {
  crumbs: Crumb[];
  running: number;
  session: Session;
  gitlabLinked?: boolean;
};

export function Header({ crumbs, running, session, gitlabLinked }: Props) {
  const { ref, width } = useElementSize();
  const narrow = width > 0 && width < NARROW;
  const navigate = useNavigate();
  const { name, logout } = useAuthContext();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const { configured, openPreferences } = useAnalytics();
  const runningLabel = `${running} running`;

  return (
    <header ref={ref} className={classes.header}>
      <nav aria-label="Breadcrumb" style={{ flex: "1 1 0", minWidth: 120, overflow: "hidden" }}>
        <ol className={classes.crumbs}>
          {crumbs.map((c, i) => {
            const last = i === crumbs.length - 1;
            return (
              <li key={`${i}-${c.label}`}>
                {i > 0 && <IconSlash size={14} className={classes.sep} aria-hidden />}
                {last || !c.to ? (
                  <span aria-current={last ? "page" : undefined} title={c.label}>
                    {c.label}
                  </span>
                ) : (
                  <Link to={c.to} title={c.label}>
                    {c.label}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </nav>

      <button
        type="button"
        className={classes.search}
        data-narrow={narrow || undefined}
        onClick={openPalette}
        aria-label="Search or jump to"
      >
        <IconSearch size={16} style={{ flex: "none" }} />
        {!narrow && (
          <>
            <span>Search or jump to</span>
            <Kbd>⌘K</Kbd>
          </>
        )}
      </button>

      <button
        type="button"
        className={classes.hbtn}
        // The count spans every emulator you can see, so open the matching tab.
        onClick={() => navigate(session.role === "developer" ? "/emulators" : "/emulators?tab=all")}
        aria-label={runningLabel}
        title={runningLabel}
      >
        <StatusDot tone="success" pulse="slow" />
        {!narrow && <span>{runningLabel}</span>}
      </button>

      <TenantSwitcher session={session} narrow={narrow} />

      <button
        type="button"
        className={classes.iconBtn}
        onClick={openFeedback}
        aria-label="Send feedback"
        title="Send feedback"
      >
        <IconMessage2 size={16} />
      </button>

      <Menu position="bottom-end" width={240} offset={8}>
        <Menu.Target>
          <button type="button" className={classes.avatar} aria-label="Account menu">
            {initialsOf(name ?? session.email)}
          </button>
        </Menu.Target>
        <Menu.Dropdown p={6}>
          <div className={classes.menuHead}>
            <div style={{ fontWeight: 600 }}>{name}</div>
            <div className="na-mono" style={{ fontSize: 12, lineHeight: "18px", color: "var(--na-fg-muted)" }}>
              {session.email}
            </div>
            <div style={{ fontSize: 12, color: "var(--na-fg-dimmed)", marginTop: 4 }}>
              {ROLE_LABEL[session.role]}
            </div>
          </div>
          <Menu.Item leftSection={<IconUser size={16} />} onClick={() => navigate("/account/profile")}>
            Profile
          </Menu.Item>
          <Menu.Item
            leftSection={<IconBrandGitlab size={16} />}
            onClick={() => navigate("/account/accounts")}
            rightSection={
              gitlabLinked === undefined ? null : (
                <span style={{ fontSize: 11, color: gitlabLinked ? "var(--na-success)" : "var(--na-warning)" }}>
                  {gitlabLinked ? "Linked" : "Not linked"}
                </span>
              )
            }
          >
            Connected accounts
          </Menu.Item>
          <Menu.Item
            leftSection={colorScheme === "dark" ? <IconSun size={16} /> : <IconMoon size={16} />}
            onClick={toggleColorScheme}
          >
            {colorScheme === "dark" ? "Light theme" : "Dark theme"}
          </Menu.Item>
          {configured && (
            <Menu.Item leftSection={<IconShieldCheck size={16} />} onClick={openPreferences}>
              Privacy settings
            </Menu.Item>
          )}
          <Menu.Divider />
          <Menu.Item leftSection={<IconMessage2 size={16} />} onClick={openFeedback}>
            Send feedback
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item leftSection={<IconLogout size={16} />} c="var(--na-fg-dimmed)" onClick={logout}>
            Sign out
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </header>
  );
}

function TenantSwitcher({ session, narrow }: { session: Session; narrow: boolean }) {
  const [opened, setOpened] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const { tenant, tenants } = session;
  const list = tenants.filter((t) =>
    `${t.name} ${t.id}`.toLowerCase().includes(q.toLowerCase()),
  );
  const label = tenant?.name ?? "No tenant";

  if (session.isLoading) {
    return (
      <div className={classes.hbtn} role="status" aria-busy="true" aria-label="Loading tenant" style={{ cursor: "default" }}>
        <Skeleton height={20} width={20} radius={0} />
        {!narrow && <Skeleton height={10} width={72} />}
      </div>
    );
  }

  // Nothing to switch between: a plain button, linking to the org page for admins.
  if (!session.isGlobalAdmin && tenants.length <= 1) {
    const content = (
      <>
        <span className={classes.tenantIni}>{initialsOf(label)}</span>
        {!narrow && <span>{label}</span>}
      </>
    );
    return session.isAdmin ? (
      <Link to="/org" className={classes.hbtn} title={label} aria-label={`Organization · ${label}`}>
        {content}
      </Link>
    ) : (
      <div className={classes.hbtn} title={label} style={{ cursor: "default" }}>
        {content}
      </div>
    );
  }

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      width={300}
      offset={8}
      trapFocus
    >
      <Popover.Target>
        <button
          type="button"
          className={classes.hbtn}
          aria-haspopup="listbox"
          aria-label={`Switch tenant · ${label}`}
          title={label}
          onClick={() => setOpened((o) => !o)}
          style={{ paddingRight: 8 }}
        >
          <span className={classes.tenantIni}>{initialsOf(label)}</span>
          {!narrow && <span>{label}</span>}
          <IconSelector size={14} style={{ color: "var(--na-fg-muted)", flex: "none" }} />
        </button>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            height: 38,
            padding: "0 12px",
            borderBottom: "1px solid var(--na-border)",
          }}
        >
          <IconSearch size={14} style={{ color: "var(--na-fg-muted)" }} />
          <input
            data-autofocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Find tenant"
            aria-label="Find tenant"
            style={{ flex: 1, minWidth: 0, background: "none", border: 0, outline: "none", fontSize: 13, color: "var(--na-fg)" }}
          />
        </label>
        <div role="listbox" aria-label="Tenants" style={{ padding: 4, maxHeight: 300, overflow: "auto" }}>
          {list.map((t) => {
            const selected = t.id === tenant?.id;
            return (
              <button
                key={t.id}
                type="button"
                role="option"
                aria-selected={selected}
                className={classes.tenantRow}
                onClick={() => {
                  session.setTenant(t.id!);
                  setOpened(false);
                }}
              >
                <span className={classes.tenantBox}>{initialsOf(t.name)}</span>
                <span style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 500 }}>
                  {t.name}
                  {t.manage && (
                    <IconShieldCheck size={14} aria-label="Admin" style={{ color: "var(--na-fg-muted)", flex: "none" }}>
                      <title>Admin</title>
                    </IconShieldCheck>
                  )}
                </span>
                <IconCheck size={14} style={{ opacity: selected ? 1 : 0 }} />
              </button>
            );
          })}
          {list.length === 0 && (
            <div style={{ padding: 14, textAlign: "center", fontSize: 12, color: "var(--na-fg-muted)" }}>
              No tenants match
            </div>
          )}
        </div>
        {session.isGlobalAdmin && (
          <button
            type="button"
            className={classes.tenantRow}
            style={{ borderTop: "1px solid var(--na-border)", height: 38, padding: "0 12px", color: "var(--na-fg-dimmed)", fontSize: 13 }}
            onClick={() => {
              setOpened(false);
              navigate("/platform/tenants/new");
            }}
          >
            <IconBuildingPlus size={16} />
            Onboard tenant
          </button>
        )}
      </Popover.Dropdown>
    </Popover>
  );
}
